import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Copy, Palette, Upload, Save, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { useQRStore } from '../../store/useQRStore';
import { generateQRContent, validateQRContent, downloadQRCodePNG } from '../../utils/qrHelpers';
import { trackEvent } from '../analytics/GoogleAnalytics';
import { cn } from '../../utils/cn';
import { useAuth } from '../../context/AuthProvider';
import { saveStaticQR } from '../../services/staticQRService';

export const QRGenerator: React.FC = () => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [qrType, setQrType] = useState<'url' | 'text' | 'email' | 'phone' | 'sms' | 'wifi' | 'vcard' | 'event'>('url');
  const [showCustomization, setShowCustomization] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [qrTitle, setQrTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  
  // Additional fields for complex types (currently unused - for future features)
  // const [smsPhone, setSmsPhone] = useState('');
  // const [smsMessage, setSmsMessage] = useState('');
  // const [emailSubject, setEmailSubject] = useState('');
  // const [emailBody, setEmailBody] = useState('');
  // const [wifiSSID, setWifiSSID] = useState('');
  // const [wifiPassword, setWifiPassword] = useState('');
  // const [wifiEncryption, setWifiEncryption] = useState<'WPA' | 'WEP' | 'nopass'>('WPA');
  
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  const { customization, updateCustomization } = useQRStore();
  const { session } = useAuth();

  // Handle URL parameters for shareable links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const type = urlParams.get('type');
    const data = urlParams.get('data');
    const fg = urlParams.get('fg');
    const bg = urlParams.get('bg');
    const size = urlParams.get('size');
    const level = urlParams.get('level');

    if (type && data) {
      setQrType(type as any);
      setContent(decodeURIComponent(data));
    }

    if (fg || bg || size || level) {
      updateCustomization({
        ...(fg && { foregroundColor: `#${fg}` }),
        ...(bg && { backgroundColor: `#${bg}` }),
        ...(size && { size: parseInt(size) }),
        ...(level && { errorCorrectionLevel: level as any }),
      });
    }
  }, [updateCustomization]);

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setQrType(e.target.value as any);
    setContent('');
  };

  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        alert('File size must be less than 2MB');
        return;
      }
      
      setLogoFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDownload = () => {
    if (canvasRef.current && qrContent) {
      const filename = `qrcode-${Date.now()}.png`;
      downloadQRCodePNG(qrContent, customization, customization.transparentBackground, filename);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(content);
      alert(t('qr.generator.messages.copied'));
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSave = async () => {
    if (!session) {
      setSaveMessage('Please sign in to save QR codes to your account');
      return;
    }

    if (!qrTitle.trim()) {
      setSaveMessage('Please enter a title for your QR code');
      return;
    }

    if (!isValid || !qrContent) {
      setSaveMessage('Please create a valid QR code first');
      return;
    }

    setIsSaving(true);
    setSaveMessage(null);

    try {
      // Generate QR code as base64 image for storage
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const svg = document.querySelector('.qr-code-svg') as SVGElement;
      
      if (svg && ctx) {
        const svgData = new XMLSerializer().serializeToString(svg);
        const img = new Image();
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        img.onload = async () => {
          canvas.width = customization.size;
          canvas.height = customization.size;
          ctx.drawImage(img, 0, 0);
          const qrImageData = canvas.toDataURL('image/png');
          
          await saveStaticQR({
            title: qrTitle.trim(),
            content: qrContent,
            type: qrType,
            customization: {
              ...customization,
              logo: logoPreview || undefined,
            },
            qr_image: qrImageData,
          });

          setSaveMessage('QR code saved successfully!');
          setQrTitle('');
          
          // Track the save event
          trackEvent('qr_code_saved', 'qr_generator', `${qrType}_${logoPreview ? 'with_logo' : 'no_logo'}`);
          
          URL.revokeObjectURL(url);
        };
        
        img.src = url;
      } else {
        // Fallback: save without image
        await saveStaticQR({
          title: qrTitle.trim(),
          content: qrContent,
          type: qrType,
          customization: {
            ...customization,
            logo: logoPreview || undefined,
          },
        });

        setSaveMessage('QR code saved successfully!');
        setQrTitle('');
        
        // Track the save event
        trackEvent('qr_code_saved', 'qr_generator', `${qrType}_${logoPreview ? 'with_logo' : 'no_logo'}`);
      }
    } catch (error: any) {
      console.error('Failed to save QR code:', error);
      setSaveMessage(error.message || 'Failed to save QR code. Please try again.');
    } finally {
      setIsSaving(false);
      // Clear message after 3 seconds
      setTimeout(() => setSaveMessage(null), 3000);
    }
  };

  const isValid = validateQRContent(content, qrType);
  const qrContent = isValid ? generateQRContent({ content, type: qrType } as any) : '';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      {/* Input Section */}
      <Card className="border-2 border-green-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-green-50 to-blue-50">
          <CardTitle className="gradient-text text-2xl">Create QR Code</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* QR Type Selection */}
          <div>
            <label className="block text-sm font-medium mb-2">QR Code Type</label>
            <select
              value={qrType}
              onChange={handleTypeChange}
              className="w-full p-2 border border-input rounded-md bg-background"
            >
              <option value="url">Website URL</option>
              <option value="text">Plain Text</option>
              <option value="email">Email Address</option>
              <option value="phone">Phone Number</option>
              <option value="wifi">WiFi Network</option>
              <option value="vcard">Contact Card</option>
            </select>
          </div>

          {/* Content Input */}
          <Input
            label="Content"
            placeholder={
              qrType === 'url' ? 'https://example.com' :
              qrType === 'email' ? 'user@example.com' :
              qrType === 'phone' ? '+1234567890' :
              qrType === 'wifi' ? 'Network Name' :
              qrType === 'vcard' ? 'Contact Name' :
              'Enter your text here'
            }
            value={content}
            onChange={handleContentChange}
            error={content && !isValid ? 'Invalid content format' : undefined}
          />

          {/* Title Input for Saving */}
          {session && (
            <Input
              label="Title (for saving to account)"
              placeholder="My QR Code"
              value={qrTitle}
              onChange={(e) => setQrTitle(e.target.value)}
              helperText="Give your QR code a name to save it to your account"
            />
          )}

          {/* Logo Upload */}
          <div>
            <label className="block text-sm font-medium mb-2">Logo (Optional)</label>
            <div className="flex items-center space-x-4">
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
                id="logo-upload"
              />
              <label
                htmlFor="logo-upload"
                className="flex items-center space-x-2 px-4 py-2 border border-input rounded-md cursor-pointer hover:bg-accent transition-colors"
              >
                <Upload className="h-4 w-4" />
                <span>Upload Logo</span>
              </label>
              {logoFile && (
                <span className="text-sm text-muted-foreground">
                  {logoFile.name}
                </span>
              )}
            </div>
            {logoPreview && (
              <div className="mt-2">
                <img
                  src={logoPreview}
                  alt="Logo preview"
                  className="w-16 h-16 object-contain border rounded"
                />
              </div>
            )}
          </div>

          {/* Customization Toggle */}
          <Button
            variant="outline"
            onClick={() => setShowCustomization(!showCustomization)}
            className="w-full"
          >
            <Palette className="h-4 w-4 mr-2" />
            {showCustomization ? 'Hide' : 'Show'} Customization
          </Button>

          {/* Customization Panel */}
          {showCustomization && (
            <div className="space-y-4 p-4 border rounded-md bg-muted/50">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Size</label>
                  <input
                    type="range"
                    min="128"
                    max="512"
                    value={customization.size}
                    onChange={(e) => updateCustomization({ size: parseInt(e.target.value) })}
                    className="w-full"
                  />
                  <span className="text-sm text-muted-foreground">{customization.size}px</span>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Error Correction</label>
                  <select
                    value={customization.errorCorrectionLevel}
                    onChange={(e) => updateCustomization({ errorCorrectionLevel: e.target.value as any })}
                    className="w-full p-2 border border-input rounded-md bg-background"
                  >
                    <option value="L">Low (7%)</option>
                    <option value="M">Medium (15%)</option>
                    <option value="Q">Quartile (25%)</option>
                    <option value="H">High (30%)</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Foreground Color</label>
                  <input
                    type="color"
                    value={customization.foregroundColor}
                    onChange={(e) => updateCustomization({ foregroundColor: e.target.value })}
                    className="w-full h-10 border border-input rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Background Color</label>
                  <input
                    type="color"
                    value={customization.backgroundColor}
                    onChange={(e) => updateCustomization({ backgroundColor: e.target.value })}
                    className="w-full h-10 border border-input rounded-md"
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card className="border-2 border-blue-100 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-blue-50 to-green-50">
          <CardTitle className="gradient-text text-2xl">QR Code Preview</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center space-y-6">
          {isValid && qrContent ? (
            <>
              <div className="relative">
                <QRCodeSVG
                  value={qrContent}
                  size={customization.size}
                  fgColor={customization.foregroundColor}
                  bgColor={customization.backgroundColor}
                  level={customization.errorCorrectionLevel}
                  includeMargin={true}
                  className="qr-code-svg"
                />
                {logoPreview && (
                  <img
                    src={logoPreview}
                    alt="Logo"
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-12 h-12 object-contain"
                  />
                )}
              </div>
              
              {/* Save Message */}
              {saveMessage && (
                <div className={cn(
                  "p-3 rounded-md text-sm",
                  saveMessage.includes('successfully') 
                    ? "bg-green-50 border border-green-200 text-green-700"
                    : "bg-red-50 border border-red-200 text-red-700"
                )}>
                  {saveMessage}
                </div>
              )}

              <div className="flex flex-wrap gap-2 justify-center">
                <Button onClick={handleDownload} className="btn-gradient">
                  <Download className="h-4 w-4 mr-2" />
                  Download PNG
                </Button>
                <Button variant="outline" onClick={handleCopy} className="border-2 hover:border-green-300">
                  <Copy className="h-4 w-4 mr-2" />
                  Copy Content
                </Button>
                {session ? (
                  <Button 
                    onClick={handleSave}
                    disabled={isSaving || !qrTitle.trim()}
                    variant="primary"
                    className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {isSaving ? 'Saving...' : 'Save to Account'}
                  </Button>
                ) : (
                  <Button 
                    variant="outline"
                    onClick={() => window.location.href = '/account'}
                    className="border-2 border-green-300 text-green-600 hover:bg-green-50"
                  >
                    <User className="h-4 w-4 mr-2" />
                    Sign In to Save
                  </Button>
                )}
              </div>
              
              <canvas ref={canvasRef} className="hidden" />
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <div className="text-6xl mb-4">📱</div>
              <p>Enter content above to generate QR code</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
